import type { FormikProps } from "formik";
import PolicyRequired from "enums/PolicyRequired";
import CardShippingSpeed from "enums/CardShippingSpeed";
import { ManipulateType } from "utils/dayjs";
import type { Employee } from "components/common/EmployeeSearchAutoComplete";
import { CustomFieldsRecord } from "hooks/customFields.types";
import type { CardProgramCapabilities } from "hooks/useCardPrograms";

interface allowedCategories {
  id: string;
  name: string;
}

export interface Merchant {
  id: string;
  name: string;
  logo?: string;
  cachedLogo?: string;
}

export type CardMeta = {
  id?: string;
  nickname: string;
  /** Empty means the company's default program. */
  cardProgramId: string;
  user: Employee | null;
  issuePhysicalCard: boolean;
  shippingSpeed: CardShippingSpeed;
  sentUserNotifications: string;
  email: boolean;
  text: boolean;
};

export type CardPolicy = {
  presetId?: string | null;
  nameOfPreset?: string | null;
  transactionLimit: string;
  dailyLimit: string;
  weeklyLimit: string;
  monthlyLimit: string;
  totalLimit: string;
  cancelationTimeFrame: {
    timeFrameValue: number | null;
    timeFrameFrequency: ManipulateType | null;
  };
  cancelationDate?: number | null;
  /** The latest a new card's cancelation date may be: its program's expiry. */
  expirationDate?: number;
  expenseCategory: {
    id: string;
    name: string;
    level: number;
  };
  allocationType: {
    key: string;
    name: string;
  };
  allocationCode: {
    id: string;
    allocationCode: string;
  };
  receiptPolicy: PolicyRequired;
  allocationPolicy: PolicyRequired;
  allowedMerchants: Array<Merchant>;
  allowedCategories: Array<allowedCategories>;
  blockedMerchants: Array<Merchant>;
  blockedCategories: Array<allowedCategories>;
  customFields?: CustomFieldsRecord;
};

const LIMIT_FIELDS = [
  "transactionLimit",
  "dailyLimit",
  "weeklyLimit",
  "monthlyLimit",
  "totalLimit",
] as const;
const MERCHANT_LIMIT_FIELDS = [
  "allowedMerchants",
  "allowedCategories",
  "blockedMerchants",
  "blockedCategories",
] as const;

/** The policy without the values its program's card issuer cannot honor, or the same object when it has none. */
export const dropUnsupportedPolicyValues = (
  policy: CardPolicy,
  capabilities?: Partial<CardProgramCapabilities>,
): CardPolicy => {
  if (!capabilities) return policy;
  const dropped: Partial<CardPolicy> = {};
  for (const field of LIMIT_FIELDS) {
    if (capabilities[field] === false && policy[field]) dropped[field] = "";
  }
  if (capabilities.merchantLimits === false) {
    for (const field of MERCHANT_LIMIT_FIELDS) {
      if (policy[field]?.length) dropped[field] = [];
    }
  }
  return Object.keys(dropped).length ? { ...policy, ...dropped } : policy;
};

const restrictionLabelKeys: Record<string, { title: string; summary: string }> =
  {
    merchant: { title: "Allowed Merchants", summary: "Various Merchants" },
    category: { title: "Allowed Categories", summary: "Various Categories" },
    blocked: { title: "Blocked Merchants", summary: "Blocked Merchants" },
    blockedCategory: {
      title: "Blocked Categories",
      summary: "Blocked Categories",
    },
  };

export const cardMetaInitialValues: CardMeta = {
  cardProgramId: "",
  id: "",
  nickname: "",
  user: null,
  issuePhysicalCard: false,
  shippingSpeed: CardShippingSpeed.Standard,
  sentUserNotifications: "",
  email: false,
  text: false,
};

// A bare resetForm() reassigns the same initialValues reference, so SpendingLimitFields
// never re-seeds and rows the user added survive a close and reopen.
export const resetCardPolicyForm = (formik: FormikProps<CardPolicy>) =>
  formik.resetForm({ values: { ...formik.initialValues } });

export const getCardPolicyInitialValues = (
  customFieldsInitialValues?: CustomFieldsRecord,
): CardPolicy => ({
  presetId: null,
  nameOfPreset: null,
  transactionLimit: "",
  dailyLimit: "",
  weeklyLimit: "",
  monthlyLimit: "",
  totalLimit: "",
  expenseCategory: { id: "", name: "", level: 0 },
  allocationType: { key: "", name: "" },
  allocationCode: { id: "", allocationCode: "" },
  receiptPolicy: PolicyRequired.policy,
  allocationPolicy: PolicyRequired.policy,
  cancelationTimeFrame: { timeFrameValue: null, timeFrameFrequency: null },
  cancelationDate: null,
  allowedMerchants: [],
  allowedCategories: [],
  blockedMerchants: [],
  blockedCategories: [],
  customFields: customFieldsInitialValues,
});

// Not on useCardData: reaching it there mounted two useFormik instances and a lazy query.
export const hasAtLeastOneLimit = (policy: CardPolicy): boolean =>
  parseFloat(policy.transactionLimit) > 0 ||
  parseFloat(policy.dailyLimit) > 0 ||
  parseFloat(policy.weeklyLimit) > 0 ||
  parseFloat(policy.monthlyLimit) > 0 ||
  parseFloat(policy.totalLimit) > 0;

export const restrictionEntryLabel = (
  item: { name: string; type: string },
  t: (key: string) => string,
): string =>
  item.type === "blocked" || item.type === "blockedCategory"
    ? `${t("Blocked")}: ${item.name}`
    : item.name;

export const getRestrictionListLabels = (
  items: Array<{ type: string }>,
  t: (key: string) => string,
): { title: string; summary: string } => {
  const types = [...new Set(items.map((item) => item.type))].filter(
    (type) => restrictionLabelKeys[type],
  );
  if (types.length > 1) {
    return {
      title: t("Merchant Restrictions"),
      summary: t("Merchant Restrictions"),
    };
  }
  const labels =
    restrictionLabelKeys[types[0]] ?? restrictionLabelKeys.category;
  return { title: t(labels.title), summary: t(labels.summary) };
};
